import { cn } from '~/lib/utils';

export interface AnimatedImgProps extends React.ComponentPropsWithoutRef<'img'> {
  /**
   * When true, applies hover animation effects:
   * - Scale up on hover
   * - Gradient overlay fade-in
   * @default false
   */
  animate?: boolean;
  /**
   * Optional text/node that appears at bottom of image on hover.
   * When animate={false}, falls back to HTML title attribute.
   */
  tooltip?: React.ReactNode;
}

/**
 * A drop-in replacement for <img> that optionally applies hover animation effects.
 * When animate={true}, the image scales up and a gradient overlay fades in on hover.
 * When tooltip is provided with animate={true}, text appears at bottom of image on hover.
 */
export function AnimatedImg({ animate = false, tooltip, className, title, ...props }: AnimatedImgProps) {
  if (!animate) {
    // For non-animated images with tooltip, use HTML title attribute as fallback
    const titleAttr = tooltip && typeof tooltip === 'string' ? tooltip : title;
    return <img className={className} title={titleAttr} {...props} />;
  }

  // Extract rounded-* classes from className to apply to wrapper for proper clipping
  const roundedClasses = className?.match(/rounded-\S+/g)?.join(' ') ?? '';

  return (
    <div className={cn('group relative overflow-hidden', roundedClasses)}>
      <img className={cn('transition-transform duration-500 group-hover:scale-105', className)} {...props} />
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100',
          tooltip ? 'from-black/40' : 'from-black/25'
        )}
      >
        {tooltip && (
          <span className="absolute bottom-2 left-3 right-3 text-center text-sm text-white/90 drop-shadow-sm">
            {tooltip}
          </span>
        )}
      </div>
    </div>
  );
}
